import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../api";

const MasterAdminAuthContext = createContext(null);

export function MasterAdminAuthProvider({ children }) {
  const [masterToken, setMasterToken] = useState(() =>
    localStorage.getItem("printq_master_token")
  );
  const [masterAdmin, setMasterAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const masterApi = api;

  masterApi.interceptors.request.use((config) => {
    const token = localStorage.getItem("printq_master_token");

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });

  useEffect(() => {
    async function verifyMasterAdmin() {
      const token = localStorage.getItem("printq_master_token");

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const res = await masterApi.get("/master-admin/me");
        setMasterAdmin(res.data);
      } catch (err) {
        console.warn("Master admin token invalid or expired", err);
        localStorage.removeItem("printq_master_token");
        setMasterToken(null);
        setMasterAdmin(null);
      } finally {
        setLoading(false);
      }
    }

    verifyMasterAdmin();
  }, [masterToken]);

  const login = async (username, password) => {
    try {
      const res = await masterApi.post("/master-admin/login", {
        username: username.trim(),
        password,
      });

      const { token, admin } = res.data;

      localStorage.setItem("printq_master_token", token);
      setMasterToken(token);
      setMasterAdmin(admin);

      toast.success("Master Admin logged in successfully");

      return { success: true };
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        "Invalid Master Admin credentials";

      toast.error(msg);

      return {
        success: false,
        error: msg,
      };
    }
  };

  const setup = async (setupData) => {
    try {
      const res = await masterApi.post(
        "/master-admin/setup",
        setupData
      );

      const { token, admin } = res.data;

      localStorage.setItem("printq_master_token", token);
      setMasterToken(token);
      setMasterAdmin(admin);

      toast.success("Master Admin setup complete!");

      return { success: true };
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        "Master Admin setup failed";

      toast.error(msg);

      return {
        success: false,
        error: msg,
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("printq_master_token");
    setMasterToken(null);
    setMasterAdmin(null);
    toast.info("Master Admin logged out");
  };

  return (
    <MasterAdminAuthContext.Provider
      value={{
        masterToken,
        masterAdmin,
        loading,
        login,
        setup,
        logout,
        masterApi,
      }}
    >
      {children}
    </MasterAdminAuthContext.Provider>
  );
}

export function useMasterAdmin() {
  const context = useContext(MasterAdminAuthContext);

  if (!context) {
    throw new Error(
      "useMasterAdmin must be used within a MasterAdminAuthProvider"
    );
  }

  return context;
}
